import React from 'react';
import { Box, Typography } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

// ============================================================================
//  WorkflowStepper — visual indicator of where a gate-out doc is in its lifecycle.
//
//  Usage:
//    <WorkflowStepper steps={[
//      { label: 'Draft',     state: 'done',    meta: '14:30 · Saravanan' },
//      { label: 'Approved',  state: 'done',    meta: '14:32 · Auto' },
//      { label: 'Executed',  state: 'current', meta: '14:32 · pallets out' },
//      { label: 'Returned',  state: 'todo',    meta: 'when ready' },
//    ]} />
//
//  state values: 'done' | 'current' | 'todo'
// ============================================================================

const dotStyles = {
  done: {
    bgcolor: '#16a34a', color: '#ffffff',
  },
  current: {
    bgcolor: '#8E44AD', color: '#ffffff',
    boxShadow: '0 0 0 4px #f5edf7',
  },
  todo: {
    bgcolor: '#f1f5f9', color: '#94a3b8', border: '2px solid #e2e8f0',
  },
};

export default function WorkflowStepper({ steps = [] }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 0,
        bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0',
        py: 1.75, px: 3,
      }}
    >
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;
        const dotSx = dotStyles[step.state] || dotStyles.todo;
        const connectorColor =
          step.state === 'done' || step.state === 'current' ? '#16a34a' : '#e2e8f0';

        return (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', flex: isLast ? '0 0 auto' : 1 }}>
            {/* Dot + label group */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'grid', placeItems: 'center',
                  fontSize: 12, fontWeight: 700, ...dotSx,
                }}
              >
                {step.state === 'done'
                  ? <CheckIcon sx={{ fontSize: 14 }} />
                  : (idx + 1)}
              </Box>
              <Box sx={{ minWidth: 100 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                  {step.label}
                </Typography>
                {step.meta && (
                  <Typography sx={{ fontSize: 10.5, color: '#64748b', lineHeight: 1.3 }}>
                    {step.meta}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Connector line */}
            {!isLast && (
              <Box
                sx={{
                  flex: 1, height: 2, mx: 1.5,
                  bgcolor: connectorColor,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ----------------------------------------------------------------------------
//  Convenience builder: derive steps from a doc.status string
// ----------------------------------------------------------------------------
export function buildStepsFromDocStatus(docStatus, options = {}) {
  const { isShipment = false, autoApproved = true } = options;
  const flow = isShipment
    ? ['Draft', 'Pending Approval', 'Approved', 'Executed', 'Returned']
    : ['Draft', autoApproved ? 'Auto-Approved' : 'Approved', 'Executed', 'Returned'];

  // Map status → which step is current
  const currentIndex = (() => {
    switch (docStatus) {
      case 'DRAFT':            return 0;
      case 'PENDING_APPROVAL': return 1;
      case 'APPROVED':         return isShipment ? 2 : 1;
      case 'EXECUTED':         return isShipment ? 3 : 2;
      case 'RETURNED':         return isShipment ? 4 : 3;
      case 'CANCELLED':        return 0;
      default:                 return 0;
    }
  })();

  return flow.map((label, i) => ({
    label,
    state: i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'todo',
  }));
}
